import type { ReactNode } from 'react';
import classNames from 'classnames';
import ReactMarkdown from 'react-markdown';

import LoaderSkeleton from '../../../LoaderSkeleton';

import styles from './_index.module.scss';
import type { ChatMessage } from '../../utilities/types';

interface ChatMessageItemProps {
  contentToRender: ReactNode;
  customContent?: ReactNode;
  hasCustomContent: boolean;
  hasPendingSegments: boolean;
  isCompletedResponseRevealing: boolean;
  isStatusMessage: boolean;
  message: ChatMessage;
  onBubbleRef: (node: HTMLDivElement | null) => void;
  renderMarkdown: boolean;
  responseProgress?: ReactNode;
}

const ChatMessageItem = ({
  contentToRender,
  customContent,
  hasCustomContent,
  hasPendingSegments,
  isCompletedResponseRevealing,
  isStatusMessage,
  message,
  onBubbleRef,
  renderMarkdown,
  responseProgress,
}: ChatMessageItemProps) => (
  <div
    className={classNames(styles['message'], styles[`is-${message.role}`], {
      [styles['message-status']]: isStatusMessage,
    })}
  >
    <div
      className={classNames(styles['message-bubble'], {
        [styles['has-custom-content']]: hasCustomContent,
      })}
      data-custom-content={hasCustomContent || undefined}
      data-testid={`chat-message-bubble-${message.id}`}
      ref={onBubbleRef}
    >
      <div
        className={classNames(styles['message-content'], {
          [styles['is-revealing']]: isCompletedResponseRevealing,
        })}
      >
        {hasCustomContent ? (
          customContent
        ) : typeof contentToRender === 'string' ? (
          renderMarkdown ? (
            <ReactMarkdown>{contentToRender}</ReactMarkdown>
          ) : (
            contentToRender
          )
        ) : (
          contentToRender
        )}

        {hasPendingSegments && (
          <div
            className={styles['message-content-loader']}
            data-testid="chat-message-content-loader"
          >
            <LoaderSkeleton className={styles['message-loader']} height={50} width={240}>
              <rect height="8" rx="4" ry="4" width="200" x="0" y="6" />
              <rect height="8" rx="4" ry="4" width="160" x="0" y="24" />
              <rect height="8" rx="4" ry="4" width="120" x="0" y="42" />
            </LoaderSkeleton>
          </div>
        )}

        {responseProgress}
      </div>
    </div>
  </div>
);

export default ChatMessageItem;
